import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '#home' },
    { name: 'Services', href: '#services' },
    { name: 'Portfolio', href: '#portfolio' },
    { name: 'About', href: '#about' },
    { name: 'Contact', href: '#contact' }
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-400 ${
        isScrolled ? 'bg-black/95 backdrop-blur-lg border-b border-white/25' : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-[7.6923%] h-20 flex items-center justify-between">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center"
        >
          <h1 className="text-2xl font-bold text-white tracking-tight">
            Howz<span className="text-[#00FFD1]">Tech</span>
          </h1>
        </motion.div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link, index) => (
            <motion.a
              key={link.name}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              onClick={() => scrollToSection(link.href)}
              className="text-[#4D4D4D] hover:text-white text-lg font-normal cursor-pointer transition-colors duration-300"
            >
              {link.name}
            </motion.a>
          ))}
          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            onClick={() => scrollToSection('#contact')}
            className="bg-[#00FFD1] text-black px-6 py-3 font-medium text-lg transition-all duration-400 hover:bg-white hover:shadow-[0_0_20px_rgba(0,255,209,0.3)]"
          >
            Get Started
          </motion.button>
        </nav>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden text-white p-2"
        >
          {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-black/98 border-t border-white/25 overflow-hidden"
          >
            <nav className="flex flex-col px-[7.6923%] py-6 gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  onClick={() => scrollToSection(link.href)}
                  className="text-white hover:text-[#00FFD1] text-lg font-normal cursor-pointer transition-colors duration-300 py-2"
                >
                  {link.name}
                </a>
              ))}
              <button
                onClick={() => scrollToSection('#contact')}
                className="bg-[#00FFD1] text-black px-6 py-3 font-medium text-lg transition-all duration-400 hover:bg-white mt-2"
              >
                Get Started
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
